/**
 * Reservation Page Manager
 * 2 sections: Reservation Form + CTA/Rooftop
 */

import { useState, useEffect } from 'react';
import { Calendar, Clock, History, Plus, Trash2 } from 'lucide-react';
import toast from 'react-hot-toast';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { InlineText, SectionCard as InlineSectionCard } from '../../components/cms/InlineEditors';
import { VersionHistoryViewer } from '../../components/cms/VersionHistoryViewer';
import versionHistoryService from '../../services/versionHistoryService';
import cmsApiService, { type ReservationSectionDTO, type ReservationTimeSlotDTO } from '../../services/cmsApiService';

type DayOfWeek = 'WEEKDAY' | 'ALLDAYS' | 'WEEKEND';

interface TimeSlot {
  id?: number;
  time: string;
  displayLabel?: string;
  active: boolean;
  dayOfWeek: DayOfWeek;
  _isNew?: boolean;      // Track if this is a newly added slot
  _isEdited?: boolean;   // Track if this existing slot was edited
}

export default function NewReservationPageManager() {
  const [historyDialog, setHistoryDialog] = useState<{ type: string; id: number } | null>(null);
  const [loading, setLoading] = useState(true);
  const [reservationSectionId, setReservationSectionId] = useState<number | null>(null);
  const [isSavingFormSettings, setIsSavingFormSettings] = useState(false);
  const [isSavingTimeslots, setIsSavingTimeslots] = useState(false);

  const [formSection, setFormSection] = useState<ReservationSectionDTO>({
    title: 'Book Your Experience',
    subtitle: 'Reserve a Table',
    successMsg: "Reservation received! We'll confirm shortly.",
    phone: true,
    email: true,
    timeSlots: [],
  });

  const [timeSlots, setTimeSlots] = useState<TimeSlot[]>([]);
  const [newSlot, setNewSlot] = useState<{ time: string; dayOfWeek: DayOfWeek }>({ time: '', dayOfWeek: 'ALLDAYS' });
  
  // Track pending changes
  const hasPendingTimeslotChanges = timeSlots.some(s => s._isNew || s._isEdited);

  const getDayOfWeekLabel = (dayOfWeek: string) => {
    if (dayOfWeek === 'ALLDAYS') return 'All Days';
    if (dayOfWeek === 'WEEKDAY') return 'Weekday';
    return 'Weekend';
  };

  const getSlotBorderClass = (slot: TimeSlot) => {
    if (slot._isNew) {
      return 'border-blue-400 bg-blue-50';
    }
    if (slot._isEdited) {
      return 'border-yellow-400 bg-yellow-50';
    }
    return '';
  };

  const extractErrorMessage = (error: any): string => {
    if (error?.response?.data?.error) {
      return error.response.data.error;
    }
    if (error?.response?.data?.businessExceptionDescription) {
      return error.response.data.businessExceptionDescription;
    }
    if (typeof error === 'object' && error !== null) {
      try {
        const parsed = JSON.parse(error.message || '{}');
        if (parsed.error) return parsed.error;
        if (parsed.businessExceptionDescription) return parsed.businessExceptionDescription;
      } catch {
        // Failed to parse, continue
      }
    }
    return error?.message || 'An error occurred';
  };

  // Load data from API
  useEffect(() => {
    loadReservationData();
  }, []);

  const loadReservationData = async () => {
    setLoading(true);
    try {
      // Get all reservation sections
      const sections = await cmsApiService.getAllReservationSections();
      
      if (sections && sections.length > 0) {
        const section = sections[0]; // Use first section
        setReservationSectionId(section.id);
        
        setFormSection({
          title: section.title || 'Book Your Experience',
          subtitle: section.subtitle || 'Reserve a Table',
          successMsg: section.successMsg || "Reservation received! We'll confirm shortly.",
          phone: section.phone !== false,
          email: section.email !== false,
          timeSlots: section.timeSlots || [],
        });

        // Load time slots
        if (section.timeSlots && section.timeSlots.length > 0) {
          const mappedSlots: TimeSlot[] = section.timeSlots.map((slot: any) => ({
            id: slot.id,
            time: slot.time || '',
            displayLabel: slot.displayLabel || '',
            active: slot.active !== false,
            dayOfWeek: slot.dayOfWeek || 'ALLDAYS',
          }));
          setTimeSlots(mappedSlots);
        }
      } else {
        // Create initial section if none exists
        await createInitialReservationSection();
      }
    } catch (error) {
      const errorMsg = extractErrorMessage(error);
      toast.error(errorMsg);
    } finally {
      setLoading(false);
    }
  };

  const createInitialReservationSection = async () => {
    try {
      const initialSection: ReservationSectionDTO = {
        title: 'Book Your Experience',
        subtitle: 'Reserve a Table',
        successMsg: "Reservation received! We'll confirm shortly.",
        phone: true,
        email: true,
        timeSlots: [
          { time: '11:00 AM', active: true, dayOfWeek: 'ALLDAYS' },
          { time: '12:00 PM', active: true, dayOfWeek: 'ALLDAYS' },
          { time: '1:00 PM', active: true, dayOfWeek: 'ALLDAYS' },
          { time: '6:00 PM', active: true, dayOfWeek: 'ALLDAYS' },
          { time: '7:00 PM', active: true, dayOfWeek: 'ALLDAYS' },
          { time: '8:00 PM', active: true, dayOfWeek: 'ALLDAYS' },
        ],
      };
      
      const created = await cmsApiService.addReservationSection(initialSection);
      setReservationSectionId(created.id);
      setFormSection(initialSection);
      
      if (created.timeSlots) {
        setTimeSlots(created.timeSlots);
      }
      
      toast.success('Reservation section created');
    } catch (error) {
      const errorMsg = extractErrorMessage(error);
      toast.error(errorMsg);
    }
  };

  const saveSection = async () => {
    if (!reservationSectionId) {
      toast.error('No reservation section found');
      return;
    }

    setIsSavingFormSettings(true);
    try {
      const updateDto: Partial<ReservationSectionDTO> = {
        title: formSection.title,
        subtitle: formSection.subtitle,
        successMsg: formSection.successMsg,
        phone: formSection.phone,
        email: formSection.email,
      };

      await cmsApiService.updateReservationSection(reservationSectionId, updateDto);
      
      versionHistoryService.track({
        entityType: 'reservation-section',
        entityId: reservationSectionId,
        action: 'update',
        changes: { form: { old: {}, new: updateDto } },
        snapshot: updateDto as Record<string, unknown>,
        user: 'Admin',
      });
      
      toast.success('Reservation form settings saved');
    } catch (error) {
      const errorMsg = extractErrorMessage(error);
      toast.error(errorMsg);
    } finally {
      setIsSavingFormSettings(false);
    }
  };

  const addTimeSlot = async () => {
    if (!newSlot.time) {
      toast.error('Please enter a time');
      return;
    }

    // Add to state with _isNew flag instead of immediately calling API
    const tempSlot: TimeSlot = {
      time: newSlot.time,
      active: true,
      dayOfWeek: newSlot.dayOfWeek,
      _isNew: true,
    };
    
    setTimeSlots([...timeSlots, tempSlot]);
    setNewSlot({ time: '', dayOfWeek: 'ALLDAYS' });
    // toast.success('Time slot added (not saved yet - click "Save All Timeslots")');
  };

  const saveAllTimeslots = async () => {
    if (!reservationSectionId) {
      toast.error('No reservation section found');
      return;
    }

    setIsSavingTimeslots(true);
    try {
      // Create new timeslots
      const newSlots = timeSlots.filter(s => s._isNew);
      for (const slot of newSlots) {
        const dto: ReservationTimeSlotDTO = {
          time: slot.time,
          active: slot.active,
          dayOfWeek: slot.dayOfWeek,
        };
        await cmsApiService.createTimeSlot(reservationSectionId, dto);
      }

      // Update edited timeslots
      const editedSlots = timeSlots.filter(s => s._isEdited && s.id);
      for (const slot of editedSlots) {
        if (!slot.id) continue;
        const dto: ReservationTimeSlotDTO = {
          time: slot.time,
          active: slot.active,
          dayOfWeek: slot.dayOfWeek,
        };
        await cmsApiService.updateTimeSlot(slot.id, dto);
      }

      // Reload to get fresh data from backend
      await loadReservationData();
      toast.success('All timeslots saved successfully');
    } catch (error) {
      const errorMsg = extractErrorMessage(error);
      toast.error(errorMsg);
    } finally {
      setIsSavingTimeslots(false);
    }
  };

  const deleteTimeSlot = async (id: number | undefined) => {
    const performDelete = async () => {
      if (!id) {
        // For new slots without ID, just remove from state
        setTimeSlots(timeSlots.filter((s) => s.id !== undefined || s !== timeSlots.find(x => !x.id)));
        toast.success('Unsaved time slot removed');
        return;
      }

      try {
        await cmsApiService.deleteTimeSlot(id);
        setTimeSlots(timeSlots.filter((s) => s.id !== id));
        toast.success('Time slot deleted');
      } catch (error) {
        const errorMsg = extractErrorMessage(error);
        toast.error(errorMsg);
      }
    };

    toast((t) => (
      <div className="flex flex-col gap-2">
        <p className="font-medium">Are you sure you want to delete this time slot?</p>
        <div className="flex gap-2 justify-end">
          <Button
            size="sm"
            variant="blueBg"
            onClick={() => toast.dismiss(t.id)}
          >
            Cancel
          </Button>
          <Button
            size="sm"
            variant="red"
            onClick={() => {
              toast.dismiss(t.id);
              performDelete();
            }}
          >
            Delete
          </Button>
        </div>
      </div>
    ), {
      duration: 10000,
      position: 'top-center',
      style: {
        marginTop: '40vh',
      },
    });
  };

  const toggleSlotActive = async (id: number | undefined) => {
    if (!id) {
      // For new slots without ID, just toggle in state
      const slotIndex = timeSlots.findIndex(s => !s.id);
      if (slotIndex >= 0) {
        const updatedSlots = [...timeSlots];
        updatedSlots[slotIndex] = { ...updatedSlots[slotIndex], active: !updatedSlots[slotIndex].active, _isEdited: true };
        setTimeSlots(updatedSlots);
      }
      return;
    }
    
    const slot = timeSlots.find((s) => s.id === id);
    if (!slot) return;
    
    // Mark as edited instead of immediately saving
    setTimeSlots(timeSlots.map((s) => 
      s.id === id ? { ...s, active: !s.active, _isEdited: true } : s
    ));
    toast.success(`Time slot ${slot.active ? 'disabled' : 'enabled'} (not saved yet)`);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <Clock className="w-8 h-8 animate-spin mx-auto mb-2" />
          <p>Loading reservation settings...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6 p-6">
      <div className="flex items-center justify-between backdrop-blur-sm bg-white/80 dark:bg-slate-900/80 rounded-2xl p-6 border-2 border-slate-200 dark:border-slate-800 shadow-sm">
        <div>
          <h1 className="text-3xl font-bold flex items-center gap-3 bg-gradient-to-r from-slate-900 via-slate-800 to-slate-700 dark:from-white dark:via-slate-100 dark:to-slate-300 bg-clip-text text-transparent">
            <Calendar className="w-8 h-8" />
            Reservation Page Manager
          </h1>
        </div>
        <Button variant="outline" onClick={() => globalThis.location.reload()}>
          <History className="w-4 h-4 mr-2" />
          Refresh
        </Button>
      </div>

      {/* Section 1: Reservation Form */}
      <InlineSectionCard
        title="Section 1: Reservation Form"
        description="Form settings and time slots"
        icon={<Calendar className="w-5 h-5" />}
        onViewHistory={() => setHistoryDialog({ type: 'reservation-section', id: reservationSectionId || 1 })}
      >
        <InlineText
          label="Subtitle"
          value={formSection.subtitle || ''}
          onChange={(v) => setFormSection((p) => ({ ...p, subtitle: v }))}
          onSave={saveSection}
        />
        <InlineText
          label="Title"
          value={formSection.title || ''}
          onChange={(v) => setFormSection((p) => ({ ...p, title: v }))}
          onSave={saveSection}
        />
        <InlineText
          label="Success Message"
          value={formSection.successMsg || ''}
          onChange={(v) => setFormSection((p) => ({ ...p, successMsg: v }))}
          onSave={saveSection}
          multiline
          rows={2}
        />
        
        <div className="space-y-3">
          <div className="space-y-2 p-3 border rounded-lg bg-muted/30">
            <p className="text-sm font-medium mb-2">Form Field Requirements</p>
            <div className="flex items-center gap-2">
              <input
                type="checkbox"
                checked={formSection.phone || false}
                onChange={(e) => setFormSection((p) => ({ ...p, phone: e.target.checked }))}
                className="rounded"
              />
              <span className="text-sm">Require Phone Number</span>
            </div>
            <div className="flex items-center gap-2">
              <input
                type="checkbox"
                checked={formSection.email || false}
                onChange={(e) => setFormSection((p) => ({ ...p, email: e.target.checked }))}
                className="rounded"
              />
              <span className="text-sm">Require Email Address</span>
            </div>
            <div className="flex justify-end mt-3">
              <Button onClick={saveSection} disabled={isSavingFormSettings} size="sm" variant="blueBg">
                {isSavingFormSettings ? 'Saving...' : 'Save Form Settings'}
              </Button>
            </div>
          </div>
        </div>

        {/* Time Slots */}
        <div className="mt-4 space-y-3">
          <div className="flex items-center gap-2 text-sm font-medium">
            <Clock className="w-4 h-4" />
            <span>Time Slots</span>
          </div>
          
          <div className="space-y-2">
            {timeSlots.map((slot, index) => (
              <div 
                key={slot.id || `new-${index}`} 
                className={`flex items-center gap-2 p-2 border rounded hover:bg-muted/50 ${getSlotBorderClass(slot)}`}
              >
                <input
                  type="checkbox"
                  checked={slot.active}
                  onChange={() => toggleSlotActive(slot.id)}
                  className="rounded"
                />
                <span className="flex-1 text-sm font-medium">{slot.time}</span>
                <span className="text-xs text-muted-foreground px-2 py-1 bg-muted rounded">
                  {getDayOfWeekLabel(slot.dayOfWeek)}
                </span>
                {slot._isNew && (
                  <span className="text-xs text-blue-600 px-2 py-1 bg-blue-100 rounded">New</span>
                )}
                {slot._isEdited && !slot._isNew && (
                  <span className="text-xs text-yellow-600 px-2 py-1 bg-yellow-100 rounded">Edited</span>
                )}
                <Button
                  variant="red"
                  size="sm"
                  onClick={() => deleteTimeSlot(slot.id)}
                >
                  <Trash2 className="w-3 h-3 text-destructive" />
                </Button>
              </div>
            ))}
          </div>

          {/* Add New Slot */}
          <div className="flex gap-2 pt-2 border-t">
            <Input
              placeholder="e.g., 3:00 PM"
              value={newSlot.time}
              onChange={(e) => setNewSlot({ ...newSlot, time: e.target.value })}
              className="flex-1"
            />
            <select
              value={newSlot.dayOfWeek}
              onChange={(e) => setNewSlot({ ...newSlot, dayOfWeek: e.target.value as DayOfWeek })}
              className="h-9 px-2 border rounded"
            >
              <option value="ALLDAYS">All Days</option>
              <option value="WEEKDAY">Weekday</option>
              <option value="WEEKEND">Weekend</option>
            </select>
            <Button onClick={addTimeSlot} size="sm" variant="outline">
              <Plus className="w-3 h-3 mr-1" /> Add
            </Button>
          </div>

          {/* Save Timeslots Button */}
          {hasPendingTimeslotChanges && (
            <div className="flex justify-end pt-3 border-t">
              <Button
                onClick={saveAllTimeslots}
                disabled={isSavingTimeslots}
                size="sm"
                variant="blueBg"
              >
                {isSavingTimeslots ? 'Saving...' : 'Save All Timeslots'}
              </Button>
            </div>
          )}
        </div>
      </InlineSectionCard>


      {historyDialog && (
        <VersionHistoryViewer
          entityType={historyDialog.type}
          entityId={historyDialog.id}
          isOpen={true}
          onClose={() => setHistoryDialog(null)}
        />
      )}
    </div>
  );
}
